import React, { useState } from 'react';
import {
  Box,
  Heading,
  Divider,
  Flex,
  Stack,
  Button,
  useToast,
  VStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Image,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Spinner,
  HStack,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { Select as ChakraSelect } from 'chakra-react-select';
import axiosInstance from '../../../services/axios';
import { useAuth } from '../../../hooks/useAuth';
import { QRCodeCanvas } from 'qrcode.react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { components } from 'react-select';
import { useNavigate } from 'react-router-dom';

/**
 * Helper function to extract error message from API response
 * Handles FastAPI validation errors which come as arrays of objects
 */
const getErrorMessage = (error) => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    // If detail is an array (FastAPI validation errors)
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    }
    // If detail is a string
    if (typeof detail === 'string') {
      return detail;
    }
    // If detail is an object
    if (typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
  }
  // Fallback to message or generic error
  return error.response?.data?.message || error.message || 'An error occurred';
};

/**
 * LinkedArtistSettings Component
 *
 * Handles TWO scenarios:
 *
 * SCENARIO 1: Merchant in switched context viewing a linked artist
 *   - Logged in as: Merchant (auth.user.account_type === 'merchant')
 *   - Context: auth.isSwitchedContext === true
 *   - Artist data source: auth.user.switched_context
 *   - Can edit: Artist info (name, genres, bio, image)
 *   - Cannot edit: Subscription (managed by merchant)
 *
 * SCENARIO 2: Linked artist logged in directly
 *   - Logged in as: Artist (auth.user.account_type === 'artist')
 *   - Context: auth.user.merchant_id exists
 *   - Artist data source: auth.user
 *   - Can edit: Artist info (name, genres, bio, image)
 *   - Cannot edit: Subscription (managed by merchant)
 */
const LinkedArtistSettings = () => {
  const { control } = useForm();
  const toast = useToast();
  const auth = useAuth();
  const navigate = useNavigate();

  // Determine which scenario we're in
  const isMerchantViewing = auth.user?.account_type === 'merchant' && auth.isSwitchedContext;

  // Get artist data based on scenario
  const artistData = React.useMemo(() => {
    if (isMerchantViewing) {
      // SCENARIO 1: Merchant viewing - get data from switched_context
      // Note: The artist's full data needs to be fetched from backend
      return auth.user?.switched_context || {};
    } else {
      // SCENARIO 2: Linked artist viewing their own - get from auth.user
      return auth.user?.account?.profile || {};
    }
  }, [auth.user, isMerchantViewing]);

  const [merchantName, setMerchantName] = useState("your merchant partner");

  // States for Profile Update
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isLoadingGenres, setIsLoadingGenres] = useState(false);
  const [isLoadingNames, setIsLoadingNames] = useState(false);
  const [isLoadingArtistName, setIsLoadingArtistName] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  // Initialize states based on scenario
  const [imagePreview, setImagePreview] = useState(() => {
    return artistData?.image_url || 'https://via.placeholder.com/200x200?text=Artist';
  });

  const [firstName, setFirstName] = useState(() => {
    if (isMerchantViewing) {
      return artistData?.firstname || '';
    }
    return auth.user?.firstname || '';
  });

  const [lastName, setLastName] = useState(() => {
    if (isMerchantViewing) {
      return artistData?.lastname || '';
    }
    return auth.user?.lastname || '';
  });

  const [artistName, setArtistName] = useState(() => {
    return artistData?.artist_name || '';
  });

  // Manage genres select state
  const [selectedGenres, setSelectedGenres] = useState(() => {
    if (artistData?.genres && Array.isArray(artistData.genres)) {
      return artistData.genres.map((genre) => ({ value: genre, label: genre }));
    }
    return [];
  });

  // Other States
  const [smsConsent, setSmsConsent] = useState(auth.user?.isSMSenabled || false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  // Social Media States
  const [socialMediaHandles, setSocialMediaHandles] = useState(() => {
    if (artistData?.social_media_handles && Array.isArray(artistData.social_media_handles)) {
      return artistData.social_media_handles;
    }
    return [];
  });
  const [isLoadingSocial, setIsLoadingSocial] = useState(false);

  // Email/OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [isEmailFormVisible, setIsEmailFormVisible] = useState(false);

  // Password
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pLoading, setPLoading] = useState(false);
  const [isPasswordFormVisible, setIsPasswordFormVisible] = useState(false);

  // Genre options
  const genres = [
    'Afrobeats', 'AfroGospel', 'AfroRave', 'AfroFusion', 'EmoAfrobeats', 'Street-Hop',
    'Pon Pon Music', 'AfroLife', 'Highlife', 'Kuduro', 'Afrocongo', 'Gqom', 'Azonto',
    'Rock', 'Hard Rock', 'Soft Rock', 'Progressive Rock', 'Punk Rock', 'Alternative Rock',
    'Jazz', 'Bebop', 'Swing', 'Fusion', 'Free Jazz', 'Smooth Jazz',
    'Classical', 'Baroque', 'Romantic', 'Modern', 'Classical Period', 'Contemporary Classical',
    'Hip Hop', 'Rap', 'Trap', 'Gangsta Rap', 'Gospel', 'Conscious Hip Hop', 'East Coast Hip Hop', 'West Coast Hip Hop',
    'Electronic', 'House', 'Techno', 'Dubstep', 'Trance', 'Drum and Bass', 'Ambient',
    'Pop', 'Teen Pop', 'Dance Pop', 'Synth-pop', 'Electropop', 'Pop Rock',
    'R&B', 'Contemporary R&B', 'Soul', 'Funk', 'Disco', 'New Jack Swing',
    'Country', 'Bluegrass', 'Honky Tonk', 'Outlaw Country', 'Country Pop', 'Alternative Country',
    'Reggae', 'Roots Reggae', 'Dub', 'Dancehall', 'Ska', 'Rocksteady'
  ].map((genre) => ({ value: genre, label: genre }));

  /* =================
      FETCH MERCHANT INFO
  ==================*/
  React.useEffect(() => {
    const fetchMerchantInfo = async () => {
      if (isMerchantViewing) {
        // SCENARIO 1: Merchant viewing - use merchant's own company name
        setMerchantName(auth.user?.company_name || 'your merchant account');
      } else if (auth.user?.merchant_id) {
        // SCENARIO 2: Linked artist - fetch merchant's company name
        try {
          const response = await axiosInstance.get(`/users/get/user/${auth.user.merchant_id}`);
          if (response.data?.company_name) {
            setMerchantName(response.data.company_name);
          }
        } catch (error) {
          console.error('Error fetching merchant info:', error);
          // Keep default "your merchant partner" if fetch fails
        }
      }
    };

    fetchMerchantInfo();
  }, [auth.user, isMerchantViewing]);

  /* =================
      HANDLERS
  ==================*/
  const handleImageSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast({
        title: 'No image selected.',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    setIsLoadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      await axiosInstance.post('/users/update/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({
        title: 'Profile image updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating profile image.',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingImage(false);
    }
  };

  const handleGenresSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingGenres(true);
    try {
      const genresArray = selectedGenres.map(option => option.value);
      await axiosInstance.put('/users/update/genres', { genres: genresArray });
      toast({
        title: 'Genres updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating genres.',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingGenres(false);
    }
  };

  const handleNamesSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoadingNames(true);
      await axiosInstance.put('/users/update/user/names', {
        firstname: firstName,
        lastname: lastName,
      });
      toast({
        title: 'Names updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating names',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingNames(false);
    }
  };

  const handleArtistNameSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoadingArtistName(true);
      await axiosInstance.put('/users/update/artist-name', {
        artist_name: artistName,
      });
      toast({
        title: 'Artist name updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating artist name',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingArtistName(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const sendOtp = async () => {
    setOtpLoading(true);
    try {
      const response = await axiosInstance.get('users/profile/send_otp');
      if (response.data) {
        setOtpSent(true);
        toast({
          title: 'OTP sent successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error sending OTP',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangeEmail = async (event) => {
    event.preventDefault();
    if (!newEmail || !otp) return;
    try {
      setOtpLoading(true);
      const response = await axiosInstance.post('users/profile/update_email/', {
        new_email: newEmail,
        otp,
      });
      toast({
        title: response.data,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error changing email',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setOtpSent(false);
      setOtp('');
      setOtpLoading(false);
      setNewEmail('');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Passwords do not match',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setPLoading(true);
      await axiosInstance.post('users/profile/reset/password/', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      toast({
        title: 'Password reset successful',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error resetting password',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPLoading(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // Log out after changing password
      auth.logout();
      navigate('/');
    }
  };

  const handleSocialMediaSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingSocial(true);
    try {
      await axiosInstance.put('/users/update/social-media', {
        social_media_handles: socialMediaHandles
      });
      toast({
        title: 'Social media handles updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating social media handles.',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingSocial(false);
    }
  };

  const handleAddSocialMedia = () => {
    setSocialMediaHandles([...socialMediaHandles, { platform: '', handle: '', url: '' }]);
  };

  const handleRemoveSocialMedia = (index) => {
    const updated = socialMediaHandles.filter((_, i) => i !== index);
    setSocialMediaHandles(updated);
  };

  const handleSocialMediaChange = (index, field, value) => {
    const updated = [...socialMediaHandles];
    updated[index][field] = value;
    setSocialMediaHandles(updated);
  };

  const handleSmsConsentChange = async () => {
    try {
      const newSmsConsent = !smsConsent;
      await axiosInstance.put(`/users/update/sms/option?update=${newSmsConsent}`);
      setSmsConsent(newSmsConsent);
      toast({
        title: 'SMS consent updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating SMS consent',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEnableAuthenticator = async () => {
    try {
      const response = await axiosInstance.get('/auth/qr-code');
      setQrCodeUrl(response.data.data);
      toast({
        title: 'Authenticator QR code generated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error generating QR code',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Loading state
  if (!auth.user) {
    return (
      <Box py={8} bg="dark.100" minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <VStack>
          <Spinner size="xl" color="primary.500" thickness="4px" />
          <Text mt={4} color="primary.500">Loading settings...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box py={8} px={4} bg="dark.100">
      <Flex direction="column" alignItems="center">
        <Heading color="dark.900" mb={6}>
          Settings
        </Heading>

        {/* Linked Artist Notice */}
        <Alert
          status="info"
          mb={8}
          maxW="800px"
          borderRadius="lg"
          bg="blue.900"
          borderColor="blue.600"
          border="1px solid"
        >
          <AlertIcon color="blue.300" />
          <Box flex={1}>
            <AlertTitle color="blue.100" fontSize="md">
              {isMerchantViewing ? 'Managing Linked Artist' : 'Linked Artist Account'}
            </AlertTitle>
            <AlertDescription color="blue.200" fontSize="sm">
              {isMerchantViewing
                ? `You are managing settings for ${artistName || 'this artist'}. Subscriptions and billing are handled through your merchant account.`
                : `Your account is managed by ${merchantName}. Subscriptions and payment methods are handled by your merchant partner.`
              }
            </AlertDescription>
          </Box>
        </Alert>

        {/* ================================
            1) ARTIST PROFILE
        =================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Artist Profile
          </Text>

          {/* Form for updating artist name (stage name) */}
          <Box mb={8}>
            <form onSubmit={handleArtistNameSubmit}>
              <FormControl isRequired>
                <FormLabel color="primary.500" fontWeight="bold">Artist Name (Stage Name)</FormLabel>
                <Input
                  value={artistName}
                  onChange={(e) => setArtistName(e.target.value)}
                  placeholder="Your artist/stage name"
                  variant="filled"
                />
              </FormControl>
              <Button type="submit" colorScheme="teal" isLoading={isLoadingArtistName} mt={4}>
                Update Artist Name
              </Button>
            </form>
          </Box>

          {/* Form for updating personal names */}
          <Box mb={8}>
            <form onSubmit={handleNamesSubmit}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="primary.500">First Name</FormLabel>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    variant="filled"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="primary.500">Last Name</FormLabel>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    variant="filled"
                  />
                </FormControl>

                <Button type="submit" colorScheme="teal" isLoading={isLoadingNames}>
                  Update Personal Names
                </Button>
              </Stack>
            </form>
          </Box>

          {/* Separate form for updating profile image */}
          <Box mb={8}>
            <form onSubmit={handleImageSubmit}>
              <FormControl mb={4}>
                <FormLabel color="primary.500">Profile Image</FormLabel>
                <Image
                  src={imagePreview}
                  alt="Artist Image"
                  boxSize="200px"
                  objectFit="cover"
                  borderRadius="lg"
                  mb={4}
                />
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  color="primary.900"
                  variant="unstyled"
                />
              </FormControl>
              <Button type="submit" colorScheme="teal" isLoading={isLoadingImage}>
                Update Image
              </Button>
            </form>
          </Box>

          {/* Separate form for updating genres */}
          <Box mb={8}>
            <form onSubmit={handleGenresSubmit}>
              <FormControl>
                <FormLabel color="primary.500">Music Genres</FormLabel>
                <Controller
                  name="genres"
                  control={control}
                  defaultValue={selectedGenres}
                  render={({ field }) => (
                    <ChakraSelect
                      {...field}
                      value={selectedGenres}
                      onChange={(value) => {
                        field.onChange(value);
                        setSelectedGenres(value);
                      }}
                      components={{
                        DropdownIndicator: (props) => (
                          <components.DropdownIndicator {...props}>
                            <ChevronDownIcon color="primary.500" />
                          </components.DropdownIndicator>
                        ),
                      }}
                      options={genres}
                      isMulti
                      closeMenuOnSelect={false}
                      placeholder="Select your music genres..."
                    />
                  )}
                />
              </FormControl>
              <Button type="submit" colorScheme="teal" isLoading={isLoadingGenres} mt={4} size="md">
                Update Genres
              </Button>
            </form>
          </Box>

          {/* Social Media Section */}
          <Box mb={8}>
            <FormLabel color="primary.500" fontWeight="bold" mb={4}>Social Media Handles</FormLabel>
            <form onSubmit={handleSocialMediaSubmit}>
              <VStack spacing={6} align="stretch">
                {socialMediaHandles.map((handle, index) => (
                  <Stack key={index} spacing={3}>
                    <FormControl isRequired>
                      <FormLabel color="primary.500">Platform</FormLabel>
                      <Input
                        value={handle.platform}
                        onChange={(e) => handleSocialMediaChange(index, 'platform', e.target.value)}
                        placeholder="e.g., Instagram, Twitter, YouTube"
                        variant="filled"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel color="primary.500">Handle/Username</FormLabel>
                      <Input
                        value={handle.handle}
                        onChange={(e) => handleSocialMediaChange(index, 'handle', e.target.value)}
                        placeholder="@username"
                        variant="filled"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel color="primary.500">Profile URL</FormLabel>
                      <Input
                        value={handle.url}
                        onChange={(e) => handleSocialMediaChange(index, 'url', e.target.value)}
                        placeholder="https://..."
                        variant="filled"
                      />
                    </FormControl>
                    <Button
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleRemoveSocialMedia(index)}
                      alignSelf="flex-end"
                    >
                      Remove
                    </Button>
                    {index < socialMediaHandles.length - 1 && <Divider />}
                  </Stack>
                ))}
              </VStack>

              <Button
                onClick={handleAddSocialMedia}
                variant="outline"
                colorScheme="teal"
                size="sm"
                mt={4}
              >
                + Add Social Media
              </Button>

              <Button type="submit" colorScheme="teal" isLoading={isLoadingSocial} mt={6} w="full">
                Update Social Media
              </Button>
            </form>
          </Box>
        </Box>

        <Divider mb={8} />

        {/* ===================================
            2) CURRENT EMAIL / CHANGE EMAIL
        =====================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={2} color="dark.900">
            Email
          </Text>
          <Text mb={4} color="primary.900">
            {isMerchantViewing ? artistData?.email : auth.user?.email}
          </Text>
          <Button
            colorScheme="teal"
            onClick={() => setIsEmailFormVisible(!isEmailFormVisible)}
          >
            {isEmailFormVisible ? 'Cancel' : 'Change Email'}
          </Button>

          {isEmailFormVisible && (
            <Box mt={4}>
              {!otpSent ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendOtp();
                  }}
                >
                  <FormControl isRequired>
                    <FormLabel color="primary.900">New Email</FormLabel>
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      variant="filled"
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    mt={4}
                    w="200px"
                    isLoading={otpLoading}
                  >
                    Send OTP
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleChangeEmail}>
                  <FormControl isRequired>
                    <FormLabel color="primary.900">Enter OTP</FormLabel>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      variant="filled"
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    mt={4}
                    w="200px"
                    isLoading={otpLoading}
                  >
                    Change Email
                  </Button>
                </form>
              )}
            </Box>
          )}
        </Box>

        <Divider mb={8} />

        {/* =================================
            3) CHANGE PASSWORD
        ===================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Password
          </Text>
          <Button
            onClick={() => setIsPasswordFormVisible(!isPasswordFormVisible)}
          >
            {isPasswordFormVisible ? 'Cancel' : 'Change Password'}
          </Button>

          {isPasswordFormVisible && (
            <VStack spacing={4} align="flex-start" w="100%" mt={4}>
              <form onSubmit={handleResetPassword} style={{ width: '100%' }}>
                <FormControl isRequired>
                  <FormLabel color="primary.900">Old Password</FormLabel>
                  <Input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    variant="filled"
                  />
                </FormControl>

                <FormControl isRequired mt={4}>
                  <FormLabel color="primary.900">New Password</FormLabel>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    variant="filled"
                  />
                </FormControl>

                <FormControl isRequired mt={4}>
                  <FormLabel color="primary.900">Confirm New Password</FormLabel>
                  <Input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    variant="filled"
                  />
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="green"
                  mt={4}
                  w="full"
                  isLoading={pLoading}
                >
                  Change Password
                </Button>
              </form>
            </VStack>
          )}
        </Box>

        <Divider mb={8} />

        {/* Additional Security */}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Additional Security
          </Text>

          {/* SMS Consent */}
          <FormControl display="flex" alignItems="center" mb={6}>
            <FormLabel mb="0" color="primary.500">
              Opt-in for SMS Notifications?
            </FormLabel>
            <Switch
              isChecked={smsConsent}
              onChange={handleSmsConsentChange}
              colorScheme="teal"
            />
          </FormControl>

          {/* MFA / Authenticator */}
          <Button onClick={handleEnableAuthenticator} colorScheme="teal">
            View MFA QR Code
          </Button>
          {qrCodeUrl && (
            <Box mt={4}>
              <QRCodeCanvas value={qrCodeUrl} size={200} />
            </Box>
          )}
        </Box>

        <Divider mb={8} />

        {/* ===================================
            SUBSCRIPTION - Managed by Merchant
        =====================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Subscription
          </Text>
          <Alert
            status="info"
            borderRadius="lg"
            bg="purple.50"
            borderColor="purple.200"
            border="1px solid"
          >
            <AlertIcon color="purple.500" />
            <Box flex={1}>
              <AlertTitle color="purple.800" fontSize="md">
                Managed by {merchantName}
              </AlertTitle>
              <AlertDescription color="purple.600" fontSize="sm">
                Your subscription and billing are handled by your merchant partner.
                Please contact {merchantName} for subscription-related inquiries.
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
      </Flex>
    </Box>
  );
};

export default LinkedArtistSettings;
